/* eslint-disable react-refresh/only-export-components */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Frown } from 'lucide-react';
import PlayerHeader from './PlayerHeader';
import { detectPlatform, getEmbedUrl, needsServerExtraction } from '../utils/video';
import { NARROW, isPhoneDevice } from '../frameSize';
import { previewVideoUrl, techniqueIdFromPreviewUrl } from '../api/preview';

const API_BASE = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || "";

// --- Mobile detection ---
// Deliberately a *viewport* test, not a panel test: this gates the floating-PiP
// mode, which only exists when there's desktop chrome around the phone panel.
// The threshold matches the NARROW breakpoint the layout uses, so a narrow
// desktop window (which drops the panel) behaves consistently.
export function isMobile() {
  // isPhoneDevice() first: with "Request desktop site" on, the UA is a desktop
  // one AND the viewport reads ~980, so both of the other tests say desktop and
  // a phone gets floating PiP windows it can't drag.
  return isPhoneDevice()
    || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    || window.innerWidth <= NARROW;
}

// --- Public API ---
export function openPip(url: string, name: string, addWindow: (entry: PipEntry) => void) {
  addWindow({ id: `${Date.now()}`, url, name });
}

export interface PipEntry {
  id: string;
  url: string;
  name: string;
  subtitle?: string;
  techniqueId?: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  /** Direct video URL — bypasses API fetch when provided (used for demo/preview). */
  directSrc?: string;
  /** When true, the player shows a play button instead of auto-fetching. */
  dormant?: boolean;
  /** Marks a set of windows opened together (a collection / category grid) so
      they can be closed as a group. */
  groupId?: string;
  /** Human label for the group (collection name / category), shown on the group bar. */
  groupTitle?: string;
}

// --- Draggable PiP Window ---
interface PipWidgetProps {
  url: string;
  name: string;
  subtitle?: string;
  onClose: () => void;
  onMove?: (x: number, y: number) => void;
  onResize?: (w: number, h: number) => void;
  style?: React.CSSProperties;
  /** When provided, use this video URL directly instead of fetching via the API. */
  directSrc?: string;
  /** When true, show a play button instead of auto-fetching the video. */
  dormant?: boolean;
  onWake?: () => void;
  /** Start with audio muted (used for demo/preview). */
  muted?: boolean;
  /** Called when user taps the star to mark for practice */
  onStar?: () => void;
  /** Whether this technique is already starred */
  starred?: boolean;
  /** Bumped to a new value to replay a bounce animation (clip already open). */
  bounceNonce?: number;
  /** Render as a flush, fixed-size tile (group grid) — flat corners, edge-to-edge video. */
  tiled?: boolean;
  /** Poster handle, for the header's credit link back to the original post. */
  author?: string;
  /** Sport rules note for the header. */
  legality?: string;
  /** The athlete in the clip, when credited separately from the poster. */
  attribution?: string;
  /** Opens the creator's page in the app from the credit line. */
  onAuthor?: (handle: string) => void;
  /** Technique id, so the header can offer a copy-link button. */
  techniqueId?: string;
}

export function PipWidget({ url, name, subtitle, onClose, onMove, onResize, style, directSrc, dormant, onWake, muted, onStar, starred, bounceNonce, tiled, author, attribution, legality, onAuthor, techniqueId }: PipWidgetProps) {
  // Replay a bounce whenever bounceNonce changes (re-clicking an open clip).
  const [bouncing, setBouncing] = useState(false);
  useEffect(() => {
    if (!bounceNonce) return;
    setBouncing(true);
    const t = setTimeout(() => setBouncing(false), 450);
    return () => clearTimeout(t);
  }, [bounceNonce]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(() => {
    if (style?.top != null && style?.left != null) {
      const x = Number(style.left);
      const y = Number(style.top);
      // Clamp restored positions to current viewport
      return {
        x: Math.max(-140, Math.min(x, window.innerWidth - 60)),
        y: Math.max(0, Math.min(y, window.innerHeight - 36)),
      };
    }
    return null;
  });
  // Re-sync to externally-driven position changes (e.g. dragging the whole
  // group via its bar). Skipped while this window is being dragged on its own.
  useEffect(() => {
    if (drag.current) return;
    if (style?.top == null || style?.left == null) return;
    setPos({ x: Number(style.left), y: Number(style.top) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style?.top, style?.left]);

  const platform = detectPlatform(url);
  const useEmbed = platform ? !needsServerExtraction(platform) : false;
  const embedUrl = useEmbed ? getEmbedUrl(url) : null;

  const [videoUrl, setVideoUrl] = useState<string | null>(directSrc || null);
  const [loading, setLoading] = useState(!directSrc && !dormant && !useEmbed);
  const [error, setError] = useState<null | 'broken' | 'rate-limited' | 'auth-expired'>(null);
  // One frame for everything. The window is always this shape and the clip
  // letterboxes or pillarboxes inside it, whatever it was shot in — the PiP
  // never reshapes itself per clip. Portrait, because the catalog is mostly
  // reels; a 16:9 clip is the case that pays for it, sitting as a band across
  // the middle. Change this one number to change every player's shape.
  const FRAME_RATIO = 9 / 16;

  useEffect(() => {
    const el = widgetRef.current;
    if (!el || !onResize) return;
    const ro = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      onResize(width, 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [onResize]);

  const retried = useRef(false);

  const fetchVideo = useCallback(async (fresh = false) => {
    // Free preview: a guest's techniques carry an opaque handle instead of the
    // source post, because the source list never leaves the server in bulk. The
    // handle is resolved by id through the preview endpoint. FeedPlayer got
    // this when the preview shipped; this player did not, so anything played
    // from Home or the Library said "This link is broken".
    const previewId = techniqueIdFromPreviewUrl(url);
    if (previewId) {
      const resolved = await previewVideoUrl(previewId);
      if (!resolved) throw new Error('broken');
      return resolved;
    }

    const base = API_BASE
      ? `${API_BASE}?url=${encodeURIComponent(url)}`
      : `/api/ig-video?url=${encodeURIComponent(url)}`;
    const endpoint = fresh ? `${base}&fresh=1` : base;
    const { auth } = await import('../firebase');
    const token = await auth.currentUser?.getIdToken();
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

    // TikTok goes through the same JSON path as Instagram — the function hands
    // back a CDN URL and the browser fetches it. No byte proxy, by policy.
    const resp = await fetch(endpoint, { headers });
    if (resp.status === 401 || resp.status === 403) throw new Error('auth');
    if (resp.status === 429) throw new Error('rate');
    const data = await resp.json();
    if (!data.videoUrl) throw new Error('broken');
    return data.videoUrl as string;
  }, [url]);

  useEffect(() => {
    if (directSrc || dormant || useEmbed) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchVideo();
        if (!cancelled) setVideoUrl(result);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : '';
        if (msg === 'auth') setError('auth-expired');
        else if (msg === 'rate') setError('rate-limited');
        else setError('broken');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [url, directSrc, dormant, useEmbed]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button, video, a')) return;
    const el = widgetRef.current;
    if (!el) return;
    e.preventDefault();
    el.setPointerCapture(e.pointerId);
    const rect = el.getBoundingClientRect();
    drag.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top };
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    setPos({ x: drag.current.origX + dx, y: drag.current.origY + dy });
  }, []);

  const onPointerUp = useCallback(() => {
    if (drag.current && pos) {
      const el = widgetRef.current;
      const w = el?.offsetWidth || 200;
      const toolbarH = 36; // height of the pip-toolbar
      const clamped = {
        x: Math.max(-w + 60, Math.min(pos.x, window.innerWidth - 60)),
        y: Math.max(0, Math.min(pos.y, window.innerHeight - toolbarH)),
      };
      setPos(clamped);
      onMove?.(clamped.x, clamped.y);
    }
    drag.current = null;
  }, [pos, onMove]);

  const posStyle: React.CSSProperties = pos
    ? { position: 'fixed', top: pos.y, left: pos.x, right: 'auto', bottom: 'auto' }
    : {};

  // Every window is portrait now, so they all take the narrow width — 440px on
  // a 9:16 frame would be nearly 800px tall and run off the screen.
  const defaultWidth = 220;

  const mobile = isMobile();

  // On mobile: fullscreen overlay, no drag, no resize
  if (mobile) {
    return (
      <div
        className="pip-mobile-fullscreen"
        ref={widgetRef}
      >
        <PlayerHeader
          title={name}
          subtitle={subtitle}
          starred={starred}
          onStar={onStar}
          onClose={onClose}
          sourceUrl={url}
          author={author}
          attribution={attribution}
          legality={legality}
          onAuthor={onAuthor}
          techniqueId={techniqueId}
        />
        <div className="pip-mobile-video">
          {loading && (
            <div className="pip-status">
              <svg className="pip-spinner" width="32" height="32" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.15"/>
                <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
          )}
          {error && (
            <div className="pip-status pip-status--error">
              <Frown size={32} />
              <span>{error === 'rate-limited' ? 'Too many requests' : error === 'auth-expired' ? 'Sign in again' : 'This link is broken'}</span>
            </div>
          )}
          {embedUrl && (
            <iframe
              src={embedUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
              allowFullScreen
              allow="autoplay; encrypted-media"
              title={name}
            />
          )}
          {/* YouTube paints its title and share links across the top of the
              frame, right under our header. The feed hides them behind a
              black bar; this player gets the same one. */}
          {embedUrl && platform === 'youtube' && <div className="pip-mobile-yt-bar" />}
          {videoUrl && !embedUrl && (
            <video
              ref={videoRef}
              src={videoUrl}
              autoPlay
              loop
              playsInline
              controls
              muted={muted}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={async () => {
                if (retried.current) { setError('broken'); return; }
                retried.current = true;
                try {
                  const freshUrl = await fetchVideo(true);
                  setVideoUrl(freshUrl);
                } catch { setError('broken'); }
              }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`pip-widget${tiled ? ' pip-widget--tiled' : ''}${bouncing ? ' pip-widget--bounce' : ''}`}
      ref={widgetRef}
      onPointerMove={tiled ? undefined : onPointerMove}
      onPointerUp={tiled ? undefined : onPointerUp}
      // Tiled tiles flow inside the group grid (CSS-sized) — no fixed position,
      // no drag. Floating PiPs keep their draggable fixed-position behaviour.
      style={tiled ? style : { ...style, ...posStyle, ...(defaultWidth && !style?.width ? { width: defaultWidth } : {}) }}
    >
      <PlayerHeader
        title={name}
        subtitle={subtitle}
        starred={starred}
        onStar={onStar}
        onClose={onClose}
        onPointerDown={tiled ? undefined : onPointerDown}
        sourceUrl={url}
        author={author}
        attribution={attribution}
        legality={legality}
        onAuthor={onAuthor}
        techniqueId={techniqueId}
      />
      <div
        className={`pip-video-container${useEmbed ? ' pip-video-container--embed' : ''}`}
        style={tiled ? undefined : { aspectRatio: `${FRAME_RATIO}` }}
      >
        {dormant && !useEmbed && (
          <button className="pip-status pip-dormant" onClick={() => { setLoading(true); onWake?.(); }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21" /></svg>
          </button>
        )}
        {loading && (
          <div className="pip-status">
            <svg className="pip-spinner" width="32" height="32" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.15"/>
              <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        )}
        {error && (
          <div className="pip-status pip-status--error">
            <Frown size={32} />
            <span>{error === 'rate-limited' ? 'Too many requests, try again in a few minutes' : error === 'auth-expired' ? 'Request was rejected. Sign in again.' : 'This link is broken'}</span>
          </div>
        )}
        {embedUrl && (
          <iframe
            src={embedUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            allowFullScreen
            allow="autoplay; encrypted-media"
            title={name}
          />
        )}
        {videoUrl && !embedUrl && (
          <video
            ref={videoRef}
            src={videoUrl}
            autoPlay
            loop
            playsInline
            controls
            muted={muted}
            onError={async () => {
              // CDN URL likely expired — retry once with fresh extraction
              if (retried.current) { setError('broken'); return; }
              retried.current = true;
              try {
                const freshUrl = await fetchVideo(true);
                setVideoUrl(freshUrl);
              } catch {
                setError('broken');
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
